import type { FinancialVehicleV2, TilaDisclosuresV2 } from "@credit-acceptance/step6-bff-client";
import { clsx } from "clsx";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency, formatPercent, formatSignedCurrency } from "../../../../lib/money";
import LineItemRow from "../../components/LineItemRow";
import Section from "../../components/Section";
import CorrectionLetterModal from "./CorrectionLetterModal";
import {
  calculateItemizedAmountFinanced,
  calculateTotalDownPaymentDeduction,
  calculateTotalSellingPrice,
} from "./Financial.calculations";

type FinancialLineItemLabelKey =
  | "financial.lineItems.sellingPrice"
  | "financial.lineItems.accessories"
  | "financial.lineItems.totalSellingPrice"
  | "financial.lineItems.salesTax"
  | "financial.lineItems.cashDown"
  | "financial.lineItems.grossTradeIn"
  | "financial.lineItems.tradeInPayoff"
  | "financial.lineItems.totalDownPayment"
  | "financial.lineItems.vsc"
  | "financial.lineItems.gap"
  | "financial.lineItems.otherBackendProducts"
  | "financial.lineItems.totalCustomerFees"
  | "financial.lineItems.itemizedAmountFinanced";

interface FinancialLineItem {
  labelKey: FinancialLineItemLabelKey;
  amount: number;
  // Only consulted when `amount` is exactly 0 (see formatSignedCurrency).
  negativeZero?: boolean;
  isLink?: boolean;
}

// Mock data standing in for the real financial payload until this section is
// wired up to the dashboard response. Amounts are internally consistent with
// each other (Total Selling Price, Total Down Payment, and the fee items all
// roll up into Itemized Amt Financed) to mirror how the real figures reconcile.

const MOCK_SELLING_PRICE_ITEMS: FinancialLineItem[] = [
  { labelKey: "financial.lineItems.sellingPrice", amount: 32_000 },
  { labelKey: "financial.lineItems.accessories", amount: 0 },
];
const MOCK_TOTAL_SELLING_PRICE = calculateTotalSellingPrice(MOCK_SELLING_PRICE_ITEMS);

// Sales Tax feeds directly into Itemized Amt Financed — it isn't part of
// either the Selling Price or Down Payment subtotal groups.
const MOCK_SALES_TAX: FinancialLineItem = { labelKey: "financial.lineItems.salesTax", amount: 888 };

const MOCK_DOWN_PAYMENT_ITEMS: FinancialLineItem[] = [
  { labelKey: "financial.lineItems.cashDown", amount: 1600, isLink: true },
  { labelKey: "financial.lineItems.grossTradeIn", amount: 0 },
  { labelKey: "financial.lineItems.tradeInPayoff", amount: 0, negativeZero: true },
];
const MOCK_TOTAL_DOWN_PAYMENT_DEDUCTION = calculateTotalDownPaymentDeduction(MOCK_DOWN_PAYMENT_ITEMS);

const MOCK_FEE_ITEMS: FinancialLineItem[] = [
  { labelKey: "financial.lineItems.vsc", amount: 1000, isLink: true },
  { labelKey: "financial.lineItems.gap", amount: 800, isLink: true },
  { labelKey: "financial.lineItems.otherBackendProducts", amount: 1500 },
  { labelKey: "financial.lineItems.totalCustomerFees", amount: 888 },
];

const MOCK_ITEMIZED_AMOUNT_FINANCED = calculateItemizedAmountFinanced(
  MOCK_TOTAL_SELLING_PRICE,
  MOCK_TOTAL_DOWN_PAYMENT_DEDUCTION,
  [MOCK_SALES_TAX, ...MOCK_FEE_ITEMS]
);

const EMPTY_VALUE = "-";

// Zero is a legitimate value (e.g. no down payment), so only `undefined` falls back.
function formatOptional<T>(value: T | undefined, format: (value: T) => string): string {
  return value === undefined ? EMPTY_VALUE : format(value);
}

// CSS class for underlined subtotal rows in the itemization breakdown.
const underlineClassName = "border-border border-t border-dashed";

function FinancialField({ label, value }: { readonly label: ReactNode; readonly value: string }) {
  return (
    <div className="gap-(--spacing-1) flex flex-col">
      <span className="text-primary text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
        {label}
      </span>
      <span className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)">
        {value}
      </span>
    </div>
  );
}

function SubsectionHeader({ children }: { readonly children: ReactNode }) {
  return (
    <div className="bg-(--color-background-default-muted) text-primary px-(--spacing-2) py-(--spacing-1) font-(--font-weight-semibold) text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) rounded">
      {children}
    </div>
  );
}

interface FinancialProperties {
  approvalId: string;
  /** From `financial.correctionLetter.hasCorrectionLetter` on the v2 dashboard response. */
  hasCorrectionLetter: boolean;
  /** From `financial.contractDate` on the v2 dashboard response. */
  contractDate?: string;
  /** From `financial.registrationState` on the v2 dashboard response. */
  registrationState?: string;
  /** From `financial.receiptDate` (contract receipt date) on the v2 dashboard response. */
  receiptDate?: string;
  /** From `financial.tila` on the v2 dashboard response. */
  tila?: TilaDisclosuresV2;
  /** From `financial.vehicle` on the v2 dashboard response. */
  vehicle?: FinancialVehicleV2;
  /** Disables the editable controls inside popups; popups still open. */
  readOnly: boolean;
}

function Financial({
  approvalId,
  hasCorrectionLetter,
  contractDate,
  registrationState,
  receiptDate,
  tila,
  vehicle,
  readOnly,
}: FinancialProperties) {
  const { t } = useTranslation();

  const [isCorrectionLetterModalOpen, setIsCorrectionLetterModalOpen] = useState(false);

  const handleCashDownClick = () => {
    window.alert(t("financial.cashDownPlaceholderAlert"));
  };

  const handleVscClick = () => {
    window.alert(t("financial.vscPlaceholderAlert"));
  };

  const handleGapClick = () => {
    window.alert(t("financial.gapPlaceholderAlert"));
  };

  const linkHandlers: Partial<Record<FinancialLineItemLabelKey, () => void>> = {
    "financial.lineItems.cashDown": handleCashDownClick,
    "financial.lineItems.vsc": handleVscClick,
    "financial.lineItems.gap": handleGapClick,
  };

  const renderLabel = (item: FinancialLineItem) =>
    item.isLink ? (
      <button
        type="button"
        onClick={linkHandlers[item.labelKey]}
        className="text-primary cursor-pointer text-left underline underline-offset-4"
      >
        {t(item.labelKey)}
      </button>
    ) : (
      t(item.labelKey)
    );

  return (
    <Section title={t("financial.sectionTitle")}>
      {/* Header fields */}
      <div className="gap-(--spacing-4) border-border mb-(--spacing-4) pb-(--spacing-4) grid grid-cols-2 border-b sm:grid-cols-4">
        <FinancialField label={t("financial.receiptDate")} value={formatIsoDate(receiptDate) || EMPTY_VALUE} />
        <FinancialField label={t("financial.contractDate")} value={formatIsoDate(contractDate) || EMPTY_VALUE} />
        <FinancialField label={t("financial.registrationState")} value={registrationState || EMPTY_VALUE} />
        <FinancialField
          label={
            <button
              type="button"
              onClick={() => setIsCorrectionLetterModalOpen(true)}
              className="text-primary cursor-pointer text-left underline underline-offset-4"
            >
              {t("financial.correctionLetter")}
            </button>
          }
          value={hasCorrectionLetter ? t("financial.yes") : t("financial.no")}
        />
      </div>

      {/* Itemization of Amount Financed + Truth in Lending Disclosures */}
      <div className="gap-x-(--spacing-8) gap-y-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
        <div className="flex flex-col">
          <SubsectionHeader>{t("financial.itemizationSectionTitle")}</SubsectionHeader>

          {/* Selling price */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            {MOCK_SELLING_PRICE_ITEMS.map((item) => (
              <LineItemRow key={item.labelKey} label={t(item.labelKey)} value={formatSignedCurrency(item.amount)} />
            ))}
            <LineItemRow
              label={t("financial.lineItems.totalSellingPrice")}
              value={formatSignedCurrency(MOCK_TOTAL_SELLING_PRICE)}
              className="font-(--font-weight-semibold)"
            />
          </div>

          {/* Sales tax + down payment */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            <LineItemRow
              label={
                <span className="flex flex-col">
                  <span>{t("financial.lineItems.salesTax")}</span>
                  <span className="text-muted-foreground text-[10px]">{t("financial.salesTaxManuallyEntered")}</span>
                </span>
              }
              value={formatSignedCurrency(MOCK_SALES_TAX.amount)}
            />
            {MOCK_DOWN_PAYMENT_ITEMS.map((item) => (
              <LineItemRow
                key={item.labelKey}
                label={renderLabel(item)}
                value={formatSignedCurrency(item.amount, item.negativeZero)}
              />
            ))}
          </div>

          {/* Total down payment */}
          <LineItemRow
            label={t("financial.lineItems.totalDownPayment")}
            value={formatSignedCurrency(MOCK_TOTAL_DOWN_PAYMENT_DEDUCTION, true)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          {/* Fees and backend products */}
          <div className="gap-(--spacing-1) mt-(--spacing-2) flex flex-col">
            {MOCK_FEE_ITEMS.map((item) => (
              <LineItemRow key={item.labelKey} label={renderLabel(item)} value={formatSignedCurrency(item.amount)} />
            ))}
          </div>

          {/* Itemized amount financed */}
          <LineItemRow
            label={t("financial.lineItems.itemizedAmountFinanced")}
            value={formatCurrency(MOCK_ITEMIZED_AMOUNT_FINANCED)}
            className={clsx("font-(--font-weight-bold) mt-(--spacing-2) pt-(--spacing-2)", underlineClassName)}
            valueClassName="text-primary"
          />

          <p className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) mt-(--spacing-3)">
            {t("financial.salesTaxFootnote")}
          </p>
        </div>

        {/* Truth in Lending Disclosures */}
        <div className="flex flex-col">
          <SubsectionHeader>{t("financial.truthInLendingSectionTitle")}</SubsectionHeader>

          {/* Vehicle */}
          <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2 sm:grid-cols-3">
            <FinancialField label={t("financial.truthInLending.newUsed")} value={vehicle?.condition || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.year")} value={formatOptional(vehicle?.year, String)} />
            <FinancialField label={t("financial.truthInLending.make")} value={vehicle?.make || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.model")} value={vehicle?.model || EMPTY_VALUE} />
            <FinancialField label={t("financial.truthInLending.vin")} value={vehicle?.vin || EMPTY_VALUE} />
            <FinancialField
              label={t("financial.truthInLending.mileage")}
              value={formatOptional(vehicle?.mileage, (mileage) => mileage.toLocaleString())}
            />
          </div>

          {/* Loan terms */}
          <div className="gap-(--spacing-4) border-border mt-(--spacing-4) pt-(--spacing-3) grid grid-cols-2 border-t sm:grid-cols-3">
            <FinancialField
              label={t("financial.truthInLending.apr")}
              value={formatOptional(tila?.apr, formatPercent)}
            />
            <FinancialField
              label={t("financial.truthInLending.financeCharge")}
              value={formatOptional(tila?.financeCharge, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.amountFinanced")}
              value={formatOptional(tila?.amountFinanced, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.totalOfPayments")}
              value={formatOptional(tila?.totalOfPayments, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.downPayment")}
              value={formatOptional(tila?.totalDownPayment, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.totalSalePrice")}
              value={formatOptional(tila?.totalSalePrice, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.intRate")}
              value={formatOptional(tila?.interestRate, formatPercent)}
            />
            <FinancialField
              label={t("financial.truthInLending.numberOfPayments")}
              value={formatOptional(tila?.term, String)}
            />
            <FinancialField
              label={t("financial.truthInLending.amountOfPayments")}
              value={formatOptional(tila?.monthlyPayment, formatCurrency)}
            />
            <FinancialField
              label={t("financial.truthInLending.paymentDueDate")}
              value={formatIsoDate(tila?.firstPaymentDate) || EMPTY_VALUE}
            />
          </div>
        </div>
      </div>

      <CorrectionLetterModal
        open={isCorrectionLetterModalOpen}
        onOpenChange={setIsCorrectionLetterModalOpen}
        approvalId={approvalId}
        readOnly={readOnly}
      />
    </Section>
  );
}

export default Financial;
